import { AuthenticatedHttp } from '@/http/axios';
import { isRequestCanceledError } from '@/utils/api';

const normalizeErrorMessage = (message = '') =>
  String(message)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

export const isFiscalDataConfigNotFoundError = (error) => {
  const normalizedDetail = normalizeErrorMessage(error?.response?.data?.detail);

  return (
    error?.response?.status === 400 &&
    normalizedDetail.includes('dados fiscais nao encontrado')
  );
};

export const getFiscalDataConfig = async (options = {}) =>
  AuthenticatedHttp.get('/financial/fiscal-data/', options)
    .then((response) => response.data)
    .catch((error) => {
      if (isRequestCanceledError(error)) {
        throw error;
      }

      if (isFiscalDataConfigNotFoundError(error)) {
        return null;
      }

      throw error;
    });

export const saveFiscalDataConfig = async (payload) =>
  AuthenticatedHttp.post('/financial/fiscal-data/', payload).then((response) => response.data);

export const updateFiscalDataConfig = async (payload) =>
  AuthenticatedHttp.put('/financial/fiscal-data/', payload).then((response) => response.data);

/**
 * A chave da conta (`habilita_nfse`). Depois da escolha por produto (CKTMF-522) ela só decide a
 * nota que a pessoa emite como coprodutora nos produtos de outras pessoas.
 */
export const updateAutomaticFiscalNote = async (payload) =>
  AuthenticatedHttp.patch('/financial/fiscal-data/toggle-nfse/', payload).then(
    (response) => response.data
  );

export const getCNPJData = async (cnpj, options = {}) =>
  AuthenticatedHttp.get('/financial/fiscal-data/company-info/', { params: { cnpj }, ...options }).then(
    (response) => response.data
  );

export const getFiscalNotes = async (params = {}, options = {}) =>
  AuthenticatedHttp.get('/financial/fiscal-data/note/', { params, ...options }).then(
    (response) => response.data
  );

// O filtro vai na query, como na listagem: é de lá que o backend o lê. No corpo
// ele era ignorado, e o zip saía com as notas mais recentes de qualquer filtro.
export const downloadAllFiscalNotes = async (params = {}) =>
  AuthenticatedHttp.post('/financial/fiscal-data/bulk-download/', null, { params }).then(
    (response) => response.data
  );

export const cancelFiscalNote = async (noteId) =>
  AuthenticatedHttp.delete('/financial/fiscal-data/note/', {
    params: { note_id: noteId },
  }).then((response) => response.data);

/**
 * Pede a reemissão das notas recusadas escolhidas na tabela.
 *
 * O backend só põe as notas na fila e responde na hora; o envio à prefeitura
 * sai em segundo plano, uma nota por vez. Volta `{ queued, rejected }`: as que
 * entraram na fila e as que não podem ser reemitidas, cada uma com o motivo.
 * Quando nenhuma entra, a resposta é 400 com o mesmo corpo.
 */
export const reemitFiscalNotes = async (noteIds) =>
  AuthenticatedHttp.post('/financial/fiscal-data/note/reemit/', { note_ids: noteIds }).then(
    (response) => response.data
  );

export const previewFiscalNote = async () =>
  AuthenticatedHttp.post('/financial/fiscal-data/test-invoice/').then((response) => response.data);

/**
 * Onde está a nota de teste que libera a emissão de notas da empresa.
 *
 * Com a nota ainda processando, o backend pergunta à Focus antes de responder:
 * consultar de novo é o que faz o desfecho aparecer mesmo sem webhook.
 */
export const getTestInvoiceStatus = async (options = {}) =>
  AuthenticatedHttp.get('/financial/fiscal-data/test-invoice/', options).then(
    (response) => response.data
  );

/**
 * As notas que o coprodutor logado deve ao produtor e ainda não emitiu.
 *
 * Aparecem quando ele não tinha cadastro fiscal no momento da venda: a emissão
 * automática não saiu e o repasse daquela venda ficou retido.
 */
export const getCoproducerPendingInvoices = async (options = {}) =>
  AuthenticatedHttp.get('/financial/fiscal-data/coproducer-pending-invoices/', options).then(
    (response) => response.data
  );

/**
 * Emite a nota que faltava de uma venda.
 *
 * Não destrava o repasse na hora: a nota sai em processamento e o dinheiro
 * só é liberado quando a prefeitura autoriza.
 *
 * Idempotente do lado do servidor: chamar de novo devolve o motivo em vez de
 * emitir uma segunda nota.
 */
export const emitCoproducerPendingInvoice = async (paymentId) =>
  AuthenticatedHttp.post(
    `/financial/fiscal-data/coproducer-pending-invoices/${paymentId}/emit/`
  ).then((response) => response.data);

/**
 * Agregados do dashboard de notas fiscais, com o mesmo filtro da listagem
 * (`toFilterParams`).
 *
 * As datas são opcionais e inclusivas, no formato `yyyy-MM-dd`. Omitir as duas
 * pede o intervalo em aberto, que o backend resolve da primeira nota emitida.
 */
export const getInvoicesDashboard = async (params = {}) =>
  AuthenticatedHttp.get('/financial/fiscal-data/dashboard/', { params }).then(
    (response) => response.data
  );
